import { get, last } from 'lodash';
import { useMemo } from 'react';
import {
  skipToken,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';

import { api } from 'services/api';

type Filters = {
  borrowerId?: string | number | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

type PaginationConfig = { limit?: number };

type FetchBorrowersParam = {
  filters: Filters;
  config: PaginationConfig;
  pageParam?: number;
};

type Borrower = {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  createdAt: string; // Might be a date string?
  partnerName: string;
};

type PaginatedResponse = {
  data: Borrower[];
  count: number;
  /** If null, there is no next offset value. */
  nextOffset: number | null;
};

/**
 *
 * Api requests for React-Query
 *
 */

const fetchBorrowers = async ({
  filters,
  config,
  pageParam,
}: FetchBorrowersParam) => {
  const { borrowerId, email, firstName, lastName } = filters;
  const result = await api.get<PaginatedResponse>('/borrowers', {
    params: {
      borrowerId: borrowerId === '' ? null : borrowerId,
      email: email === '' ? null : email,
      firstName: firstName === '' ? null : firstName,
      lastName: lastName === '' ? null : lastName,
      limit: get(config, 'limit', 20),
      offset: pageParam ?? 0,
    },
  });

  return {
    borrowers: result.data.data,
    nextOffset: result.data.nextOffset,
    count: result.data.count,
  };
};

const fetchBorrower = async ({ borrowerId }: { borrowerId: string }) => {
  const borrowerRes = await api.get(`/borrowers/${borrowerId}`);

  return borrowerRes?.data.data;
};

const deleteBorrower = async ({
  borrowerId,
}: {
  borrowerId: string | number;
}) => {
  const borrowerRes = await api.delete(`/borrowers/${borrowerId.toString()}`);

  return (borrowerRes as any)?.success;
};

const resubscribeBorrower = async ({
  borrowerId,
}: {
  borrowerId: string | number;
}) => {
  const resubscribeRes = await api.delete(
    `/borrowers/${borrowerId.toString()}/sendgrid-global-unsubscribe`,
  );

  return resubscribeRes?.data;
};

export const verifyBorrowerResidency = async ({
  borrowerId,
  bypassReason,
}: {
  borrowerId: string | number;
  bypassReason: any;
}) => {
  const verificationMethod = 'admin-override';

  const res = await api.put(`/borrowers/${borrowerId.toString()}/residency`, {
    verificationMethod,
    bypassReason,
  });

  return res.data;
};

/**
 *
 * Custom Hooks with React-Query
 *
 */

type UseBorrowersParam = {
  filters?: Filters;
  config?: PaginationConfig;
};

export const useBorrowers = ({
  filters = {},
  config = { limit: 20 },
}: UseBorrowersParam = {}) => {
  const {
    data,
    isPending: isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['borrowers', filters, config] as const,
    queryFn: ({ pageParam }) => fetchBorrowers({ filters, config, pageParam }),
    initialPageParam: 0,
    getNextPageParam: lastPage => {
      return lastPage.nextOffset ?? undefined;
    },
    refetchOnWindowFocus: false,
  });

  const resultArray = data?.pages;

  const borrowers = useMemo(
    () => resultArray?.flatMap(result => result.borrowers),
    [resultArray],
  );

  const borrowersTotalCount = last(resultArray)?.count ?? 0;

  return {
    borrowers,
    borrowersTotalCount,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

export const useBorrower = (borrowerId: string | number) => {
  return useQuery({
    queryKey: ['borrower', borrowerId],
    queryFn:
      typeof borrowerId === 'string'
        ? () => fetchBorrower({ borrowerId: borrowerId })
        : skipToken,
    staleTime: 1000 * 60 * 15,
  });
};

export const useMutateBorrower = (action: 'delete' | 'resubscribe') => {
  const queryClient = useQueryClient();
  let query;
  let mutationKey;
  switch (action) {
    case 'delete':
      query = deleteBorrower;
      mutationKey = ['deleteBorrower'];
      break;
    case 'resubscribe':
      query = resubscribeBorrower;
      mutationKey = ['resubscribeBorrower'];
      break;
  }

  return useMutation({
    mutationKey,
    mutationFn: query,
    onSuccess: async (_, variables: { borrowerId: string | number }) => {
      // borrowerId need to be string because when we create the user query we use borrowerId from query params
      await queryClient.invalidateQueries({
        queryKey: ['borrower', variables.borrowerId.toString()],
      });
    },
  } satisfies UseMutationOptions<
    unknown,
    Error,
    { borrowerId: string | number }
  >);
};
